import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PhoneInput } from "@/components/molecules/phone";
import {
  SearchableSelect,
  type SearchableSelectItem,
} from "@/components/molecules/searchable-select";
import { ScheduleSelect } from "@/components/molecules/schedule-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAssignableDevices } from "@/features/devices/hooks/use-devices";
import { useConnectors } from "@/features/connectors/hooks/use-connectors";
import { useUpdateMemberProfile } from "@/features/clients/hooks/use-clients";
import { CalendarAPI } from "@/features/calendar/api/calendar";
import type { Calendar } from "@/features/calendar/types";
import { ExternalAccountsAPI } from "@/features/external-accounts/api/external-accounts";
import type { ExternalAccount } from "@/features/external-accounts/types";
import type { ClientMemberWithUser } from "@/features/clients/types";
import { PencilIcon, CalendarBlankIcon } from "@phosphor-icons/react";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

interface EditProfileDialogProps {
  member: ClientMemberWithUser;
  triggerButton?: React.ReactNode;
}

/**
 * Sentinel for "their mobile". Radix `Select` cannot hold an empty string as an
 * item value — it is how it spells "nothing selected" — and "no handset" is a
 * real answer here, not an absence of one.
 */
const MOBILE = "__mobile__";

export function EditProfileDialog({
  member,
  triggerButton,
}: EditProfileDialogProps) {
  const { t } = useTranslation("members");
  const { company: clientId } = useParams<{ company: string }>();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState(
    member.membership.display_name || "",
  );
  const [scheduleId, setScheduleId] = useState(
    member.membership.schedule_id || "NONE",
  );
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [accounts, setAccounts] = useState<ExternalAccount[]>([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>(
    member.membership.calendars_ids?.ids || [],
  );
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [phone, setPhone] = useState(member.membership.phone || "");
  // "" is the mobile, an id is a handset. Kept as a single value rather than a
  // toggle plus a select: they are two spellings of one answer — where do we
  // call this person — and a toggle would let both be set at once.
  const [deviceId, setDeviceId] = useState(member.membership.device_id || "");
  const [sipExtension, setSipExtension] = useState(
    member.membership.sip_extension || "",
  );
  const [acceptCallTransfers, setAcceptCallTransfers] = useState(
    member.membership.accept_call_transfers ?? false,
  );
  const [roleText, setRoleText] = useState(member.membership.role_text || "");

  const updateProfile = useUpdateMemberProfile();

  // Only fetched while the dialog is open: every member may read this list, so
  // it would otherwise be one request per row of the members table.
  const { data: devices = [], isLoading: devicesLoading } =
    useAssignableDevices(clientId || "", open);

  // An extension only means something when HeyUp is registered on the
  // client's own PBX — a SIP channel. Without one there is nothing to dial it
  // through, so the field is not offered (a stored value is kept and shown).
  const { data: connectors = [] } = useConnectors(clientId || "");
  const hasPbx =
    connectors.some(
      (connector) => connector.type === "sip" && connector.status === "active",
    ) || !!member.membership.sip_extension;

  // A handset that has since been deleted keeps its slot, otherwise saving an
  // unrelated field would silently move the member back to their mobile.
  const deviceOptions = useMemo(() => {
    const known = devices.filter((device) => device.status !== "suspended");
    return deviceId && !known.some((device) => device.id === deviceId)
      ? [
          ...known,
          {
            id: deviceId,
            label: t("editProfile.deviceUnknown"),
            status: "provisioning" as const,
            registered: false,
          },
        ]
      : known;
  }, [devices, deviceId, t]);

  useEffect(() => {
    const loadCalendars = async () => {
      if (open && clientId) {
        try {
          setLoadingCalendars(true);
          const [cals, accts] = await Promise.all([
            CalendarAPI.listCalendars(clientId),
            ExternalAccountsAPI.getAccounts(clientId),
          ]);
          setCalendars(cals);
          setAccounts(accts);
        } catch (error) {
          console.error("Failed to load calendars:", error);
          toast.error(t("editProfile.calendarsError"));
        } finally {
          setLoadingCalendars(false);
        }
      }
    };
    loadCalendars();
  }, [open, clientId]);

  // Convert calendars to SearchableSelectItem format
  const calendarItems: SearchableSelectItem[] = useMemo(
    () =>
      calendars
        .map((calendar) => {
          const account = accounts.find(
            (a) => a.id === calendar.external_account_id,
          );
          if (!account) return null;

          const accountName = [
            account?.name,
            account?.provider,
            calendar.description,
          ]
            .filter(Boolean)
            .join(" - ");

          return {
            id: calendar.id,
            label: calendar.name,
            description: accountName,
            icon: <CalendarBlankIcon className="h-4 w-4 text-primary" />,
            badge: calendar.is_primary ? t("common.primary") : undefined,
            data: calendar,
          };
        })
        .filter(Boolean) as SearchableSelectItem[],
    [calendars, accounts],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // The member, never the account attached to it: since the split those are
    // two different ids, and this dialog edits the member.
    const memberId = member.membership.id;
    if (!clientId || !memberId) return;

    try {
      await updateProfile.mutateAsync({
        clientId,
        memberId,
        updates: {
          display_name: displayName || undefined,
          schedule_id: scheduleId === "NONE" ? null : scheduleId,
          calendars_ids: { ids: selectedCalendarIds },
          phone: phone || undefined,
          device_id: deviceId,
          // "" takes them off the PBX; only sent where the field is shown.
          ...(hasPbx ? { sip_extension: sipExtension.trim() } : {}),
          accept_call_transfers: acceptCallTransfers,
          role_text: roleText || undefined,
        },
      });
      toast.success(t("messages.profileUpdated"));
      setOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("messages.profileUpdateError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button variant="ghost" size="sm">
            <PencilIcon className="h-4 w-4" weight="fill" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("editProfile.title")}</DialogTitle>
            <DialogDescription>
              {t("editProfile.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="display_name">
                {t("editProfile.displayName")}
              </Label>
              <Input
                id="display_name"
                placeholder={t("editProfile.displayNamePlaceholder")}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role_text">{t("editProfile.roleText")}</Label>
              <Input
                id="role_text"
                placeholder={t("editProfile.roleTextPlaceholder")}
                value={roleText}
                onChange={(e) => setRoleText(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="schedule">{t("editProfile.schedule")}</Label>
              <ScheduleSelect
                value={scheduleId}
                onChange={setScheduleId}
                allowNone={true}
                placeholder={t("editProfile.schedulePlaceholder")}
              />
            </div>
            <div className="flex items-start gap-3 py-2">
              <Checkbox
                id="accept_call_transfers"
                checked={acceptCallTransfers}
                onCheckedChange={(checked) =>
                  setAcceptCallTransfers(checked === true)
                }
              />
              <div className="flex flex-col gap-1">
                <Label
                  htmlFor="accept_call_transfers"
                  className="cursor-pointer font-normal"
                >
                  {t("editProfile.acceptCallTransfers")}
                </Label>
              </div>
            </div>
            {acceptCallTransfers && (
              <>
                {hasPbx && (
                  <div className="grid gap-2">
                    <Label htmlFor="member-sip-extension">
                      {t("editProfile.sipExtension")}
                    </Label>
                    <Input
                      id="member-sip-extension"
                      inputMode="numeric"
                      placeholder="102"
                      value={sipExtension}
                      onChange={(e) => setSipExtension(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("editProfile.sipExtensionHelp")}
                    </p>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="member-device">
                    {t("editProfile.device")}
                  </Label>
                  <Select
                    value={deviceId || MOBILE}
                    onValueChange={(value) =>
                      setDeviceId(value === MOBILE ? "" : value)
                    }
                    disabled={devicesLoading}
                  >
                    <SelectTrigger id="member-device">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={MOBILE}>
                        {t("editProfile.deviceMobile")}
                      </SelectItem>
                      {deviceOptions.map((device) => (
                        <SelectItem key={device.id} value={device.id}>
                          {device.label}
                          {device.registered
                            ? ""
                            : ` — ${t("editProfile.deviceOffline")}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t("editProfile.deviceDescription")}
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="phone">{t("editProfile.phone")}</Label>
                  <PhoneInput
                    id="phone"
                    placeholder={t("editProfile.phonePlaceholder")}
                    value={phone}
                    onChange={(value) => setPhone(value || "")}
                  />
                  <p className="text-xs text-muted-foreground">
                    {deviceId
                      ? t("editProfile.phoneWithDeviceHelp")
                      : t("editProfile.phoneHelp")}
                  </p>
                </div>
              </>
            )}
            <div className="grid gap-2">
              <Label>{t("editProfile.calendars")}</Label>
              <p className="text-xs text-muted-foreground mb-2">
                {t("editProfile.calendarsDescription")}
              </p>
              <SearchableSelect
                items={calendarItems}
                value={selectedCalendarIds}
                onChange={(v) =>
                  setSelectedCalendarIds(Array.isArray(v) ? v : [v])
                }
                multiple={true}
                placeholder={t("editProfile.calendarsPlaceholder")}
                searchPlaceholder={t("editProfile.calendarsSearch")}
                emptyText={t("editProfile.calendarsEmpty")}
                isLoading={loadingCalendars}
                allowNone={false}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("editProfile.cancel")}
            </Button>
            <Button type="submit" disabled={updateProfile.isPending}>
              {updateProfile.isPending
                ? t("editProfile.saving")
                : t("editProfile.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
